import { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../App';
import { usePermissions } from '@/hooks/usePermissions';
import { Label } from '@/components/ui/Label';
import { api } from '../services/api';
import { PageHeader } from '@/components/PageHeader';
import { DataTable } from '@/components/DataTable';
import { EmptyState } from '@/components/EmptyState';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/Dialog';
import { Skeleton } from '@/components/ui/Skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { FileText, CheckCircle, XCircle, Send, ClipboardCheck, LockOpen } from 'lucide-react';

interface TimesheetWeek { id: string; isoWeek: string; status: 'DRAFT' | 'SUBMITTED' | 'APPROVED' | 'REJECTED'; submittedAt: string | null; user?: { id: string; name: string }; }

function statusVariant(status: string) {
  switch (status) { case 'APPROVED': return 'success' as const; case 'REJECTED': return 'destructive' as const; case 'SUBMITTED': return 'warning' as const; default: return 'secondary' as const; }
}

export default function TimesheetsPage() {
  const { user } = useContext(AuthContext)!;
  const { t } = useTranslation();
  const isAdmin = user?.globalRole === 'ADMIN';
  const { canWrite, canAdmin } = usePermissions({ pageKey: 'timesheets' });

  const [weeks, setWeeks] = useState<TimesheetWeek[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectComment, setRejectComment] = useState('');
  const [rejectWeekId, setRejectWeekId] = useState('');

  const [unlockOpen, setUnlockOpen] = useState(false);
  const [unlockReason, setUnlockReason] = useState('');
  const [unlockWeekId, setUnlockWeekId] = useState('');

  async function fetchWeeks() {
    try {
      setLoading(true);
      const data: TimesheetWeek[] = await api(`/timesheets${isAdmin ? '?isoWeek=' : ''}`);
      setWeeks(data);
    } catch (err: any) { setError(err.message || 'Failed to load'); } finally { setLoading(false); }
  }

  useEffect(() => { fetchWeeks(); }, []);

  async function handleSubmit(isoWeek: string) {
    try { await api('/timesheets/submit', { method: 'POST', body: JSON.stringify({ isoWeek }) }); fetchWeeks(); }
    catch (err: any) { setError(err.message || 'Submit failed'); }
  }

  async function handleApprove(id: string) {
    try { await api(`/timesheets/${id}/approve`, { method: 'POST', body: '{}' }); fetchWeeks(); }
    catch (err: any) { setError(err.message || 'Approve failed'); }
  }

  async function handleReject() {
    if (!rejectComment.trim()) return;
    try { await api(`/timesheets/${rejectWeekId}/reject`, { method: 'POST', body: JSON.stringify({ comment: rejectComment }) }); setRejectOpen(false); setRejectComment(''); setRejectWeekId(''); fetchWeeks(); }
    catch (err: any) { setError(err.message || 'Reject failed'); }
  }

  async function handleUnlock() {
    if (!unlockReason.trim()) return;
    try { await api(`/timesheets/${unlockWeekId}/unlock`, { method: 'POST', body: JSON.stringify({ reason: unlockReason }) }); setUnlockOpen(false); setUnlockReason(''); setUnlockWeekId(''); fetchWeeks(); }
    catch (err: any) { setError(err.message || 'Unlock failed'); }
  }

  const myWeeks = weeks.filter(w => w.user?.id === user?.id || !w.user);
  const pendingWeeks = weeks.filter(w => w.status === 'SUBMITTED' || (isAdmin && w.status === 'APPROVED'));

  const myColumns = [
    { key: 'isoWeek', header: 'Week' },
    { key: 'status', header: 'Status', render: (w: TimesheetWeek) => <Badge variant={statusVariant(w.status)}>{w.status}</Badge> },
    { key: 'actions', header: '', className: 'w-10', render: (w: TimesheetWeek) => w.status === 'DRAFT' && canWrite ? <Button data-testid="submit-timesheet-button" variant="outline" size="sm" onClick={() => handleSubmit(w.isoWeek)}><Send className="mr-1 h-3.5 w-3.5" /> {t('submit')}</Button> : null },
  ];

  const pendingColumns = [
    { key: 'user', header: 'User', render: (w: TimesheetWeek) => w.user?.name || '-' },
    { key: 'isoWeek', header: 'Week' },
    { key: 'status', header: 'Status', render: (w: TimesheetWeek) => <Badge variant={statusVariant(w.status)}>{w.status}</Badge> },
    { key: 'actions', header: '', className: 'w-32', render: (w: TimesheetWeek) => (
      w.status === 'APPROVED' ? (
        <Button data-testid="unlock-timesheet-button" variant="outline" size="sm" className="h-7 gap-1 text-amber-700" onClick={() => { setUnlockWeekId(w.id); setUnlockOpen(true); }}><LockOpen className="h-3.5 w-3.5" /> Unlock</Button>
      ) : (
        <div className="flex items-center gap-1">
          <Button data-testid="approve-button" variant="outline" size="sm" className="h-7 gap-1 text-emerald-700" onClick={() => handleApprove(w.id)}><CheckCircle className="h-3.5 w-3.5" /> Approve</Button>
          <Button data-testid="reject-button" variant="ghost" size="sm" className="h-7 gap-1 text-rose-600" onClick={() => { setRejectWeekId(w.id); setRejectOpen(true); }}><XCircle className="h-3.5 w-3.5" /> Reject</Button>
        </div>
      )
    )},
  ];

  return (
    <div className="animate-in space-y-6">
      <PageHeader title={t('timesheets') || 'Timesheets'} />
      {error && <Badge variant="destructive">{error}</Badge>}
      {loading ? <div className="space-y-2"><Skeleton className="h-10 w-full" /><Skeleton className="h-12 w-full" /></div> : (
        <Tabs defaultValue="my">
          <TabsList>
            <TabsTrigger value="my"><FileText className="mr-2 h-4 w-4" /> My Weeks</TabsTrigger>
            {canAdmin && <TabsTrigger value="pending"><ClipboardCheck className="mr-2 h-4 w-4" /> Pending Approvals</TabsTrigger>}
          </TabsList>
          <TabsContent value="my" className="mt-4">
            <Card>
              <CardContent className="pt-4">
                <DataTable
                  columns={myColumns}
                  data={myWeeks}
                  keyExtractor={(w) => w.id}
                  emptyState={<EmptyState icon={FileText} title="No weeks" description="Your timesheet weeks will appear here." />}
                />
              </CardContent>
            </Card>
          </TabsContent>
          {canAdmin && (
            <TabsContent value="pending" className="mt-4">
              <Card>
                <CardContent className="pt-4">
                  <DataTable
                    columns={pendingColumns}
                    data={pendingWeeks}
                    keyExtractor={(w) => w.id}
                    emptyState={<EmptyState icon={ClipboardCheck} title="No pending approvals" description="All caught up!" />}
                  />
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      )}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reject Timesheet</DialogTitle><DialogDescription>Provide a reason for the rejection.</DialogDescription></DialogHeader>
          <div className="space-y-2 py-2">
            <Label>Reason</Label>
            <Input value={rejectComment} onChange={e => setRejectComment(e.target.value)} placeholder="Enter rejection reason..." />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)}>Cancel</Button>
            <Button data-testid="confirm-reject-button" variant="destructive" onClick={handleReject} disabled={!rejectComment.trim()}>Reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={unlockOpen} onOpenChange={setUnlockOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Unlock Timesheet</DialogTitle><DialogDescription>Provide a reason for unlocking this approved timesheet.</DialogDescription></DialogHeader>
          <div className="space-y-2 py-2">
            <Label>Reason</Label>
            <Input value={unlockReason} onChange={e => setUnlockReason(e.target.value)} placeholder="Enter unlock reason..." />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUnlockOpen(false)}>Cancel</Button>
            <Button data-testid="confirm-unlock-button" variant="default" onClick={handleUnlock} disabled={!unlockReason.trim()}>Unlock</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
